import {
  Button,
  Divider,
  Grid,
  makeStyles,
  Typography
} from '@material-ui/core';
import ExpandLessSharp from '@material-ui/icons/ExpandLessSharp';
import ExpandMoreSharp from '@material-ui/icons/ExpandMoreSharp';
import ReportProblemOutlinedIcon from '@material-ui/icons/ReportProblemOutlined';
import Skeleton from '@material-ui/lab/Skeleton';
import { PropTypes } from 'prop-types';
import React, { useMemo, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { TaskStatus } from '~/types';
import { themeWithoutDir as theme } from '~/modules/App/withRootTheme';
import { NoValue, User } from '~/modules/common/components';
import ErrorCard from '~/modules/tasks/modecs/ErrorCard';
import Date from '~/modules/common/components/Date';
import DateRange from '~/modules/common/components/DateRange';
import Decimal from '~/modules/common/components/Decimal';
import { RoleName } from '~/modules/common/components/entities/role';
import { resolveValueOrDefault } from '~/modules/common/components/FormCustomField';
import ReadOnlyContainer from '~/modules/common/components/ReadOnlyContainer';
import ExtensionFieldValueText from '~/modules/extensionFields/components/ExtensionFieldValueText';
import { useMeContext } from '~/modules/me';
import { useTaskCustomFieldDefinitions } from '~/modules/tasks/modecs/useTaskCustomFieldDefinitions';
import { useDialogState } from '~/modules/common/hooks';
import RescheduleTaskDialog from '~/modules/tasks/components/RescheduleTaskDialog';
import { useSetTaskTimeEntryDateRange } from '~/modules/tasks/hooks';
import { ProjectRoleReferenceInfoOnProjectRateTable } from '../common';
import {
  TaskStatusSelectField,
  ClosedOnDatePicker,
  ReadOnlyCustomFieldValue,
  RescheduleHeaderInfo
} from './components';
import { useExpandedState } from './hooks';
import TaskDrawerHeaderInfoBarExpanded from './TaskDrawerHeaderInfoBarExpanded';
import RescheduleButton from './RescheduleButton';

const useStyles = makeStyles({
  wrapper: {
    display: 'flex',
    paddingLeft: theme.spacing(2),
    paddingBottom: theme.spacing(1)
  },
  flexRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'inherit',
    flexWrap: 'wrap',
    paddingTop: theme.spacing(1)
  },
  rowItem: {
    display: 'flex',
    paddingRight: theme.spacing(2)
  },
  columnItem: {
    display: 'flex',
    flexDirection: 'column',
    paddingRight: 0,
    marginRight: theme.spacing(7),
    marginLeft: theme.spacing(1.5)
  },
  subheading: {
    display: 'flex',
    marginBottom: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    ...theme.typography.caption
  },
  plannedDates: {
    paddingRight: theme.spacing(1)
  },
  boldItem: {
    fontWeight: theme.typography.fontWeightMedium
  },
  smallIconButton: {
    padding: '0'
  },
  expandButton: {
    color: theme.palette.text.main,
    marginTop: theme.spacing(2.25),
    textTransform: 'none'
  },
  expanded: {
    backgroundColor: theme.palette.background.default
  },
  details: {
    paddingTop: theme.spacing(2),
    paddingLeft: theme.spacing(4),
    paddingBottom: theme.spacing(2),
    marginLeft: theme.spacing(-2),
    marginBottom: 'auto'
  },
  noDateIcon: {
    color: theme.palette.error.dark
  },
  noValue: { fontSize: theme.typography.body2.fontSize },
  description: {
    whiteSpace: 'pre-wrap',
    hyphens: 'auto',
    wordBreak: 'break-word',
    overflowWrap: 'break-word'
  },
  range: ({ hasStartAndEndDate }) => ({
    marginTop: hasStartAndEndDate ? theme.spacing(1) : theme.spacing(0.5),
    fontSize: theme.spacing(1.5),
    fontWeight: 500,
    display: hasStartAndEndDate ? 'inline' : 'flex',
    alignItems: hasStartAndEndDate ? undefined : 'center',
    color: 'inherit'
  }),
  date: {
    lineHeight: 'inherit',
    color: 'inherit',
    fontSize: 'inherit',
    fontWeight: 'inherit',
    display: 'inline'
  },
  spacer: {
    color: 'inherit',
    fontWeight: 400,
    lineHeight: 'inherit'
  },
  container: {
    overflow: 'hidden',
    marginTop: theme.spacing(1.5)
  },
  name: {
    fontSize: theme.spacing(1.5),
    fontWeight: 500,
    paddingLeft: theme.spacing(1) - 1
  }
});

export const TaskDrawerHeaderInfoBar = ({
  task,
  expandedInitialState = false,
  handleRefetch
}) => {
  const { formatMessage } = useIntl();
  const me = useMeContext();
  const {
    featureFlags: {
      isPsaRmpTaskAllocation1Enabled,
      isPsaPrpGen3PolarisTenantPhase1Enabled
    },
    tenantPermissions: { isEditTimesheetEnabled },
    isRolledUpTaskEstimateCalculationMethodEnabled,
    hasViewProjectBillingOptions,
    isPortfolioProductEnabled
  } = me;

  const {
    startDate,
    endDate,
    taskStatus,
    assignedUser,
    assignedRole,
    isClosedByTaskInheritance,
    isClosed,
    extensionFieldValues,
    initialEstimatedHours,
    timeAndExpenseEntryType,
    isTimeEntryAllowed,
    costTypeUri,
    description,
    project,
    rolledUpSummary
  } = task;

  const isTaskDateRollupEnabled = project?.isTaskDateRollupEnabled || false;
  const hasStartAndEndDate = startDate && endDate;
  const actualHours = rolledUpSummary?.actualHours;
  const rolledUpTaskStatus =
    taskStatus === TaskStatus.Completed
      ? TaskStatus.Completed
      : actualHours
      ? TaskStatus.Inprogress
      : TaskStatus.Notstarted;

  const classes = useStyles({
    status: isRolledUpTaskEstimateCalculationMethodEnabled
      ? rolledUpTaskStatus
      : taskStatus,
    hasStartAndEndDate,
    isRolledUpTaskEstimateCalculationMethodEnabled
  });

  const dateRangeClasses = useMemo(
    () => ({
      range: classes.range,
      date: classes.date,
      spacer: classes.spacer
    }),
    [classes]
  );
  const noValueClasses = useMemo(
    () => ({
      root: classes.noValue
    }),
    [classes.noValue]
  );

  const {
    open: isRescheduleTaskDialogOpen,
    openDialog: openRescheduleTaskDialog,
    closeDialog: closeRescheduleTaskDialog
  } = useDialogState(false);
  const { expanded, setExpanded } = useExpandedState(expandedInitialState);
  const { setTaskTimeEntryDateRange, loading } = useSetTaskTimeEntryDateRange();

  const {
    loading: customFieldDefsLoading,
    error: customFieldDefsError,
    customFieldDefinitions
  } = useTaskCustomFieldDefinitions();

  const onRescheduleTaskSubmit = useCallback(
    async ({
      startDate: newStartDate,
      endDate: newEnddate,
      rescheduleTaskModificationOption
    }) => {
      await setTaskTimeEntryDateRange({
        taskId: task.id,
        startDate: newStartDate,
        endDate: newEnddate,
        rescheduleTaskModificationOption,
        shouldRollupTaskDates: isTaskDateRollupEnabled
      });
      closeRescheduleTaskDialog();
      handleRefetch();
    },
    [
      setTaskTimeEntryDateRange,
      task.id,
      isTaskDateRollupEnabled,
      closeRescheduleTaskDialog,
      handleRefetch
    ]
  );

  const [canViewProject, canEditTask, canViewEstimate, canViewCostType] = [
    Boolean(
      project?.permittedActionUris?.includes(
        'urn:replicon:project-action:view-project'
      )
    ),
    Boolean(
      project?.permittedActionUris?.includes(
        'urn:replicon:project-action:edit-tasks'
      )
    ),
    Boolean(
      project?.permittedActionUris?.includes(
        'urn:replicon:project-action:view-project-estimates'
      )
    ),
    Boolean(
      project?.permittedActionUris?.includes(
        'urn:replicon:project-action:view-cost-type'
      ) &&
        (isPortfolioProductEnabled || isPsaPrpGen3PolarisTenantPhase1Enabled)
    )
  ];

  const taskOwner = useMemo(
    () =>
      canViewProject
        ? assignedUser
        : { id: me.id, uri: me.uri, displayText: me.displayText },
    [canViewProject, assignedUser, me.id, me.uri, me.displayText]
  );

  const visibleCustomFieldDefinitions = customFieldDefinitions.filter(
    def => def.isVisible
  );

  const customFields = useMemo(
    () =>
      visibleCustomFieldDefinitions.reduce(
        (fields, definition) => ({
          ...fields,
          [definition.uri]: resolveValueOrDefault(task.customFields, definition)
        }),
        {}
      ),
    [visibleCustomFieldDefinitions, task]
  );

  const showTimeAndExpenseEntryTypeField =
    hasViewProjectBillingOptions &&
    project?.billingType?.id !== 'urn:replicon:billing-type:non-billable';

  const showRescheduleButton = isPsaRmpTaskAllocation1Enabled && canEditTask;
  const isRescheduleDisabled =
    showRescheduleButton && isTaskDateRollupEnabled && task.hasChildren;

  return customFieldDefsLoading ? (
    <Skeleton data-qe-id="TaskDrawer_Loading" variant="rect" height={48} />
  ) : customFieldDefsError ? (
    <ErrorCard error={customFieldDefsError} />
  ) : (
    <div>
      <div className={classes.wrapper}>
        <div className={classes.flexRow}>
          <>
            <div className={classes.columnItem}>
              <TaskStatusSelectField task={task} canEditTask={canEditTask} />
            </div>
            <div className={classes.columnItem}>
              <span className={classes.subheading}>
                <span className={classes.plannedDates}>
                  {formatMessage({
                    id: 'taskDrawer.plannedDates'
                  })}
                </span>
                {showRescheduleButton && (
                  <RescheduleButton
                    onClick={openRescheduleTaskDialog}
                    isDisabled={isRescheduleDisabled}
                    isRolledUpTaskEstimateCalculationMethodEnabled={
                      isRolledUpTaskEstimateCalculationMethodEnabled
                    }
                  />
                )}
                {isRescheduleDisabled && (
                  <RescheduleHeaderInfo
                    isRolledUp={isRolledUpTaskEstimateCalculationMethodEnabled}
                  />
                )}
              </span>
              <DateRange
                classes={dateRangeClasses}
                start={startDate}
                end={endDate}
                noDateComponent={
                  <ReportProblemOutlinedIcon className={classes.noDateIcon} />
                }
              />
            </div>
            <div className={classes.rowItem}>
              {taskOwner && (
                <User
                  classes={{
                    container: classes.container,
                    name: classes.name
                  }}
                  user={taskOwner}
                  size="small"
                />
              )}
            </div>
          </>
          <div className={classes.rowItem}>
            <Button
              data-qe-id="TaskDrawer_MoreFields_Icon"
              size="small"
              onClick={() => setExpanded(!expanded)}
              className={classes.expandButton}
            >
              {expanded ? (
                <ExpandLessSharp className={classes.boldItem} />
              ) : (
                <ExpandMoreSharp className={classes.boldItem} />
              )}
              {formatMessage({
                id: expanded ? 'taskDrawer.less' : 'taskDrawer.more'
              })}
              {Boolean(project?.id && task?.id && !expanded) && (
                <ProjectRoleReferenceInfoOnProjectRateTable
                  projectId={project.id}
                  taskUri={task?.id}
                  projectRoleId={task?.assignedRole?.id}
                  variant="IconOnly"
                />
              )}
            </Button>
          </div>
        </div>
      </div>
      {(isClosed || isClosedByTaskInheritance) && (
        <ClosedOnDatePicker task={task} canEditTask={canEditTask} />
      )}
      {isPsaRmpTaskAllocation1Enabled && expanded && (
        <TaskDrawerHeaderInfoBarExpanded
          task={task}
          visibleCustomFieldDefinitions={visibleCustomFieldDefinitions}
          customFields={customFields}
        />
      )}
      {!isPsaRmpTaskAllocation1Enabled && expanded && (
        <div className={classes.expanded}>
          <Divider />
          <Grid container spacing={1} className={classes.details}>
            <Grid item xs={6}>
              <ReadOnlyContainer
                label={formatMessage({
                  id: 'taskDrawerEdit.startDate'
                })}
              >
                <Date value={startDate} />
              </ReadOnlyContainer>
            </Grid>
            <Grid item xs={6}>
              <ReadOnlyContainer
                label={formatMessage({
                  id: 'taskDrawerEdit.endDate'
                })}
              >
                <Date value={endDate} />
              </ReadOnlyContainer>
            </Grid>
            <Grid item xs={6}>
              <ReadOnlyContainer
                label={formatMessage({
                  id: 'taskDrawerEdit.role'
                })}
              >
                {assignedRole ? (
                  <RoleName
                    role={assignedRole}
                    showTooltip={false}
                    showRate={false}
                    className={classes.roleName}
                  />
                ) : (
                  <NoValue classes={noValueClasses} />
                )}
              </ReadOnlyContainer>
            </Grid>
            <Grid item xs={6}>
              <ReadOnlyContainer
                label={formatMessage({
                  id: 'taskDrawerEdit.taskOwner'
                })}
              >
                {taskOwner ? (
                  <User user={taskOwner} />
                ) : (
                  <NoValue classes={noValueClasses} />
                )}
              </ReadOnlyContainer>
            </Grid>
            {showTimeAndExpenseEntryTypeField && (
              <Grid item xs={6}>
                <ReadOnlyContainer
                  label={formatMessage({
                    id: 'taskDrawerEdit.timeAndExpenseEntry'
                  })}
                >
                  <Typography variant="body2">
                    {formatMessage({
                      id: `timeAndExpenseEntryOptions.${timeAndExpenseEntryType.uri}`
                    })}
                  </Typography>
                </ReadOnlyContainer>
              </Grid>
            )}
            {isEditTimesheetEnabled && (
              <Grid item xs={6}>
                <ReadOnlyContainer
                  label={formatMessage({
                    id: 'taskDrawerEdit.allowTimeEntry.label'
                  })}
                >
                  <Typography variant="body2">
                    {formatMessage({
                      id: isTimeEntryAllowed
                        ? 'taskDrawerEdit.allowTimeEntry.options.yes'
                        : 'taskDrawerEdit.allowTimeEntry.options.no'
                    })}
                  </Typography>
                </ReadOnlyContainer>
              </Grid>
            )}
            {canViewCostType && (
              <Grid item xs={6}>
                <ReadOnlyContainer
                  label={formatMessage({ id: 'taskDrawer.costType' })}
                >
                  <Typography variant="body2" data-qe-id="costType">
                    {costTypeUri ? (
                      formatMessage({
                        id: `projectFinancialsCard.${costTypeUri}`
                      })
                    ) : (
                      <NoValue />
                    )}
                  </Typography>
                </ReadOnlyContainer>
              </Grid>
            )}
            <Grid item xs={12}>
              <Divider />
            </Grid>
            <Grid item xs={12}>
              <ReadOnlyContainer
                label={formatMessage({ id: 'taskDrawerEdit.description' })}
              >
                <Typography variant="body2" className={classes.description}>
                  {description || <NoValue />}
                </Typography>
              </ReadOnlyContainer>
            </Grid>
            {visibleCustomFieldDefinitions.map(definition => (
              <Grid key={definition.uri} item xs={12} sm={6}>
                <ReadOnlyContainer label={definition.displayText}>
                  <ReadOnlyCustomFieldValue
                    definition={definition}
                    value={customFields[definition.uri]}
                  />
                </ReadOnlyContainer>
              </Grid>
            ))}
            {Boolean(extensionFieldValues?.length) &&
              extensionFieldValues.map(e => (
                <Grid item xs={12} sm={6} key={e.definition.id}>
                  <ReadOnlyContainer label={e.definition.displayText}>
                    <Typography variant="body2" className={classes.description}>
                      <ExtensionFieldValueText extensionFieldValue={e} />
                    </Typography>
                  </ReadOnlyContainer>
                </Grid>
              ))}
            {canViewEstimate && (
              <>
                <Grid item xs={12}>
                  <Divider />
                </Grid>
                <Grid item xs={12}>
                  <ReadOnlyContainer
                    label={formatMessage({
                      id: 'taskDrawerEdit.initialEstimatedHours'
                    })}
                  >
                    <Typography variant="body2">
                      <Decimal value={initialEstimatedHours} />
                    </Typography>
                  </ReadOnlyContainer>
                </Grid>
              </>
            )}
          </Grid>
        </div>
      )}
      {isPsaRmpTaskAllocation1Enabled && isRescheduleTaskDialogOpen && (
        <RescheduleTaskDialog
          onSubmit={onRescheduleTaskSubmit}
          onCancel={closeRescheduleTaskDialog}
          isSetTaskTimeEntryDateRangeLoading={loading}
          task={task}
        />
      )}
    </div>
  );
};

TaskDrawerHeaderInfoBar.propTypes = {
  task: PropTypes.object.isRequired,
  expandedInitialState: PropTypes.bool,
  handleRefetch: PropTypes.func
};

export default TaskDrawerHeaderInfoBar;
